import { useState } from "react";
import { X } from "lucide-react";

const GAME_SETS = [
  {
    id: "poodle-gifts",
    name: "Poodle's Room",
    imageA: "https://customer-assets.emergentagent.com/job_fluffy-friend-4/artifacts/m9zj7rvv_ldsartbot_just_tidy_up_some_mess_--ar_32_--v_7_650bf833-a1ce-4082-9b17-7a519a94b7a2_0.png",
    imageB: "https://customer-assets.emergentagent.com/job_fluffy-friend-4/artifacts/1stls869_ldsartbot_making_a_game_picture_spot_the_differences._--ar_32_c9164f6f-b04a-4857-9b26-e693de03eae2_1.png",
    differences: [
      { x: 24.5, y: 22, radius: 4, hint: "1" },
      { x: 13, y: 36, radius: 4, hint: "2" },
      { x: 47, y: 54, radius: 4, hint: "3" },
      { x: 29, y: 63, radius: 4, hint: "4" },
      { x: 68, y: 62, radius: 4, hint: "5" },
      { x: 8, y: 77, radius: 4, hint: "6" },
      { x: 68, y: 97, radius: 4, hint: "7" },
    ],
  },
];

const SpotDifference = ({ isOpen, onClose }) => {
  const [currentSet, setCurrentSet] = useState(0);
  const [found, setFound] = useState([]);
  const [gameState, setGameState] = useState('ready');
  const [showHint, setShowHint] = useState(false);
  const [viewingImage, setViewingImage] = useState('A');

  const set = GAME_SETS[currentSet];
  const totalDiffs = set.differences.length;

  const startGame = () => {
    setFound([]);
    setGameState('playing');
  };

  const handleImageClick = (e) => {
    if (gameState !== 'playing') return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    set.differences.forEach((diff, index) => {
      if (found.includes(index)) return;
      const distance = Math.sqrt(Math.pow(x - diff.x, 2) + Math.pow(y - diff.y, 2));
      if (distance < diff.radius) {
        setFound(prev => {
          const newFound = [...prev, index];
          if (newFound.length === totalDiffs) {
            setGameState('won');
          }
          return newFound;
        });
      }
    });
  };

  const handleTouch = (e) => {
    if (gameState !== 'playing') return;
    e.preventDefault();

    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((touch.clientX - rect.left) / rect.width) * 100;
    const y = ((touch.clientY - rect.top) / rect.height) * 100;

    set.differences.forEach((diff, index) => {
      if (found.includes(index)) return;
      const distance = Math.sqrt(Math.pow(x - diff.x, 2) + Math.pow(y - diff.y, 2));
      if (distance < diff.radius) {
        setFound(prev => {
          const newFound = [...prev, index];
          if (newFound.length === totalDiffs) {
            setGameState('won');
          }
          return newFound;
        });
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-[#FDFBF7] flex flex-col" data-testid="spot-difference-game">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-white/90 backdrop-blur-md border-b border-[#E8E5DC]">
        <h2 className="text-base font-bold text-[#4A4A4A]">Spot the Differences</h2>
        <div className="flex items-center gap-3">
          {gameState === 'playing' && (
            <span className="text-sm font-semibold text-[#B89823]">{found.length}/{totalDiffs} found</span>
          )}
          <button onClick={onClose} data-testid="spot-diff-close"
            className="p-2 rounded-full bg-[#F3F0E6] text-[#6B6B6B] hover:bg-[#E8E5DC] transition-all">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Ready screen */}
      {gameState === 'ready' && (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-sm">
            <p className="text-5xl mb-4">🔍</p>
            <h3 className="text-2xl font-bold text-[#4A4A4A] mb-2">Spot the Differences!</h3>
            <p className="text-sm text-[#6B6B6B] mb-6">
              Look carefully at the two pictures and tap where you see something different!
            </p>
            <button onClick={startGame} data-testid="spot-diff-start"
              className="px-8 py-3 bg-[#E8D4A8] text-[#5A4A32] rounded-full font-bold text-lg hover:bg-[#D9C599] transition-all hover:scale-105">
              Start!
            </button>
          </div>
        </div>
      )}

      {/* Playing and Won (same view) */}
      {(gameState === 'playing' || gameState === 'won') && (
        <div className="flex-1 overflow-auto flex flex-col items-center p-2">
          {/* Images stacked vertically */}
          <div className="w-full max-w-3xl space-y-2">
            <div className="relative inline-block w-full">
              <img src={set.imageA} alt="Picture A" className="w-full h-auto rounded-xl block" />
              <div className="absolute top-2 left-2 bg-white/80 rounded-full px-2 py-0.5 text-xs font-bold text-[#4A4A4A]">A</div>
              <div className="absolute inset-0 cursor-pointer" onClick={handleImageClick} onTouchStart={handleTouch}></div>
              {set.differences.map((diff, i) => (
                found.includes(i) && (
                  <div key={`found-a-${i}`} className="absolute w-8 h-8 rounded-full border-4 border-[#5A8A5A] pointer-events-none"
                    style={{ left: `${diff.x}%`, top: `${diff.y}%`, transform: 'translate(-50%, -50%)' }}>
                  </div>
                )
              ))}
            </div>

            <div className="relative inline-block w-full">
              <img src={set.imageB} alt="Picture B" className="w-full h-auto rounded-xl block" />
              <div className="absolute top-2 left-2 bg-white/80 rounded-full px-2 py-0.5 text-xs font-bold text-[#4A4A4A]">B</div>
              <div className="absolute inset-0 cursor-pointer" onClick={handleImageClick} onTouchStart={handleTouch}></div>
              {set.differences.map((diff, i) => (
                found.includes(i) && (
                  <div key={`found-b-${i}`} className="absolute w-8 h-8 rounded-full border-4 border-[#5A8A5A] pointer-events-none"
                    style={{ left: `${diff.x}%`, top: `${diff.y}%`, transform: 'translate(-50%, -50%)' }}>
                  </div>
                )
              ))}
            </div>
          </div>

          {/* Play Again - right under the photos */}
          {gameState === 'won' && (
            <div className="text-center py-2">
              <p className="text-sm font-bold text-[#5A8A5A] mb-2">🎉 You found them all!</p>
              <button onClick={startGame} data-testid="spot-diff-play-again"
                className="px-6 py-3 bg-[#E8D4A8] text-[#5A4A32] rounded-full font-semibold hover:bg-[#D9C599] transition-all hover:scale-105">
                Play Again
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SpotDifference;
